import React from "react";
import { getLoginUrl } from "@/services/Login";
import { useTranslation } from "react-i18next";
import { LoginValidationSchema, LoginFormInput } from "@/components/pages/Login/LoginValidationSchema";

import SVG from "@/components/atoms/SVGAsset/SVGAsset";
import CTA from "@/components/atoms/CTA";
import Heading from "@/components/atoms/Heading";
import styles from "./styles.module.css";
import { HeadersContainer, SimpleHeader } from "@/components/molecules/Header";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import TextInput from "@/components/atoms/TextInput";
import { saveRedirectionType } from "@/utils/sessionStorage";
import { useNavigate } from "react-router-dom";

function parseCallbackUrl() {
  const queryString = window.location.search;
  return new URLSearchParams(queryString);
}

const LoginCheckEmail: React.FC = () => {
  const { t } = useTranslation();

  const params = parseCallbackUrl();
  const portal = params.get("portal");
  const emailParam = params.get("email") || "";
  const signUp = params.get("signup");
  const navigate = useNavigate();

  const { setUserEmail, getPort } = useAuth();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<LoginFormInput>({
    resolver: zodResolver(LoginValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      email: emailParam,
    },
  });

  const { mutate: mutateLogin, isPending: isPendingLogin } = useMutation({
    mutationFn: (email: string) => {
      const port = getPort();
      return getLoginUrl(email, port);
    },
    onSuccess: (res, variables) => {
      const { type, url } = res.data;
      saveRedirectionType(type);
      setUserEmail(variables);
      window.location.href = url;
      navigate("/external-redirect?target=" + encodeURIComponent(url), {
        replace: true,
      });
    },
    onError: (error: any) => {
      console.error(error);
      alert("E-post-sjekk feilet: " + error);
    },
  });

  const onSubmit = (formValues: LoginFormInput) => {
    mutateLogin(formValues.email);
  };

  const validPortal = portal && portal === "replast";
  return (
    <>
      <title>Reviver - Login Check email</title>
      <meta name="description" content="Reviver - Login check email" />
      <HeadersContainer>
        <SimpleHeader />
      </HeadersContainer>
      <div className={styles.loginRoot}>
        <div className={styles.loginContent}>
          <figure className={styles.splashImg}>
            <img src="/assets/img/splash/polymer-melt-logo.png" alt="Reviver logo" />
          </figure>

          <div className={styles.formPanel}>
            {validPortal ? (
              <img src="/assets/img/portal/replast.png" width="72" alt="Reviver logo" />
            ) : (
              <SVG className={styles.logo} name="reviver-logo" />
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="30px">
                <Stack gap="10px">
                  {!validPortal && (
                    <Heading order={1} styleOrder={4}>
                      {signUp ? t("actions:signUp") : t("actions:login")}
                    </Heading>
                  )}
                  <Text color="secondary">
                    {validPortal ? t("common:portalIntro") : signUp ? t("common:signUpIntro") : t("common:logInIntro")}
                  </Text>
                </Stack>
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, value }, fieldState }) => (
                    <TextInput
                      data-testid="input-login-email"
                      id="email"
                      required
                      placeholder={t("common:email")}
                      value={value}
                      onChange={onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <CTA
                  disabled={!isValid || isPendingLogin}
                  loading={isPendingLogin}
                  data-testid="btn-login"
                  id={"btn-loginCheckEmail"}
                  type="submit"
                  className="submit"
                  size="lg"
                  fullWidth={true}
                >
                  {validPortal ? t("actions:continue") : signUp ? t("actions:signUp") : t("actions:login")}
                </CTA>
              </Stack>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginCheckEmail;
